import React, { createContext, useContext, useState, useEffect } from "react";
import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import theme, { darkTheme } from "../theme";

// Estado inicial del contexto de tema
const initialState = {
  currentTheme: "light",
  isDarkMode: false,
  primaryColor: "#d32f2f",
  customColors: {
    plant: "#2e7d32",
    process: "#1565c0",
    room: "#ef6c00",
    machine: "#5e35b1",
    catalog: "#d32f2f",
  },
  preferences: {
    compactMode: false,
    animationsEnabled: true,
    highContrast: false,
  },
};

// Crear el contexto
const ThemeContext = createContext();

// Exportar el contexto como named export
export { ThemeContext };

// Hook para usar el contexto de tema
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme debe ser usado dentro de un ThemeProvider");
  }
  return context;
};

// Provider del contexto de tema
export const ThemeProvider = ({ children }) => {
  const [themeState, setThemeState] = useState(() => {
    // Intentar cargar preferencias del localStorage
    const savedPreferences = localStorage.getItem("sgcm_theme_preferences");
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        return { ...initialState, ...parsed };
      } catch (error) {
        console.warn("Error al cargar preferencias de tema:", error);
      }
    }
    return initialState;
  });

  // Guardar preferencias en localStorage cuando cambien
  useEffect(() => {
    localStorage.setItem("sgcm_theme_preferences", JSON.stringify(themeState));
  }, [themeState]);

  // Detectar preferencia de tema oscuro del sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event) => {
      if (themeState.currentTheme === "system") {
        setThemeState((prev) => ({
          ...prev,
          isDarkMode: event.matches,
        }));
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    // Configurar estado inicial si está en modo sistema
    if (themeState.currentTheme === "system") {
      setThemeState((prev) => ({
        ...prev,
        isDarkMode: mediaQuery.matches,
      }));
    }

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeState.currentTheme]);

  // Cambiar tema
  const setCurrentTheme = (themeName) => {
    setThemeState((prev) => {
      const newState = { ...prev, currentTheme: themeName };

      switch (themeName) {
        case "light":
          newState.isDarkMode = false;
          break;
        case "dark":
          newState.isDarkMode = true;
          break;
        case "system":
          newState.isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
          break;
        default:
          newState.isDarkMode = false;
      }

      return newState;
    });
  };

  // Alternar entre tema claro y oscuro
  const toggleTheme = () => {
    setCurrentTheme(themeState.isDarkMode ? "light" : "dark");
  };

  // Actualizar color primario
  const setPrimaryColor = (color) => {
    setThemeState((prev) => ({
      ...prev,
      primaryColor: color,
    }));
  };

  // Actualizar colores personalizados
  const setCustomColors = (colors) => {
    setThemeState((prev) => ({
      ...prev,
      customColors: { ...prev.customColors, ...colors },
    }));
  };

  // Actualizar preferencias
  const updatePreferences = (newPreferences) => {
    setThemeState((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, ...newPreferences },
    }));
  };

  // Alternar modo compacto
  const toggleCompactMode = () => {
    setThemeState((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        compactMode: !prev.preferences.compactMode,
      },
    }));
  };

  // Alternar animaciones
  const toggleAnimations = () => {
    setThemeState((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        animationsEnabled: !prev.preferences.animationsEnabled,
      },
    }));
  };

  // Alternar alto contraste
  const toggleHighContrast = () => {
    setThemeState((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        highContrast: !prev.preferences.highContrast,
      },
    }));
  };

  // Restablecer a valores por defecto
  const resetToDefaults = () => {
    setThemeState(initialState);
    localStorage.removeItem("sgcm_theme_preferences");
  };

  // Obtener tema actual de Material-UI
  const getCurrentMUITheme = () => {
    return themeState.isDarkMode ? darkTheme : theme;
  };

  // Obtener color por tipo de elemento de jerarquía
  const getHierarchyColor = (type) => {
    return themeState.customColors[type] || theme.palette.primary.main;
  };

  // Obtener estilos CSS personalizados
  const getCustomStyles = () => {
    const styles = {};

    if (themeState.preferences.compactMode) {
      styles["--spacing-factor"] = "0.75";
      styles["--component-height-factor"] = "0.9";
    }

    if (!themeState.preferences.animationsEnabled) {
      styles["--transition-duration"] = "0ms";
    }

    if (themeState.preferences.highContrast) {
      styles["--contrast-ratio"] = "1.2";
    }

    return styles;
  };

  // Valor del contexto
  const contextValue = {
    // Estado
    ...themeState,

    // Acciones de tema
    setCurrentTheme,
    toggleTheme,
    setPrimaryColor,
    setCustomColors,

    // Preferencias
    updatePreferences,
    toggleCompactMode,
    toggleAnimations,
    toggleHighContrast,

    // Utilidades
    getCurrentMUITheme,
    getHierarchyColor,
    getCustomStyles,
    resetToDefaults,
  };

  // Aplicar estilos personalizados al documento
  useEffect(() => {
    const customStyles = getCustomStyles();
    const root = document.documentElement;

    Object.entries(customStyles).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    return () => {
      Object.keys(customStyles).forEach((property) => {
        root.style.removeProperty(property);
      });
    };
  }, [themeState.preferences]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={getCurrentMUITheme()}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
